import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';

import {
  BASE_PADDING_OFFSET,
  EMPTY_ARRAY_MARKER,
  EMPTY_OBJECT_MARKER,
  INDENT_PER_DEPTH
} from './constants';
import FieldValue from './FieldValue';

interface CollapsibleFieldProps {
  fieldKey: string;
  value: unknown;
  depth?: number;
  showNull?: boolean;
  expandAll?: boolean;
}

const CollapsibleField = ({
  fieldKey,
  value,
  depth = 0,
  showNull = true,
  expandAll = false
}: CollapsibleFieldProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(expandAll);
  }, [expandAll]);
  const isObject = value !== null && typeof value === 'object' && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const hasChildren =
    (isObject && Object.keys(value as object).length > 0) || (isArray && value.length > 0);

  const paddingLeft = depth * INDENT_PER_DEPTH;

  if (!isObject && !isArray) {
    if (!showNull && value === null) {
      return null;
    }

    return (
      <div
        className="py-1 px-2 hover:bg-slate-50 flex items-start"
        style={{ paddingLeft: `${paddingLeft + BASE_PADDING_OFFSET}px` }}>
        <span className="font-mono text-xs text-slate-600 font-bold mr-1 flex-shrink-0">
          {fieldKey}:
        </span>
        <span className="font-mono text-xs break-all">
          <FieldValue value={value} fieldKey={fieldKey} />
        </span>
      </div>
    );
  }

  if (!hasChildren) {
    return (
      <div
        className="py-1 px-2 hover:bg-slate-50 flex items-start"
        style={{ paddingLeft: `${paddingLeft + BASE_PADDING_OFFSET}px` }}>
        <span className="font-mono text-xs text-slate-600 font-bold mr-1 flex-shrink-0">
          {fieldKey}:
        </span>
        <span className="font-mono text-xs text-slate-400 italic">
          {isArray ? EMPTY_ARRAY_MARKER : EMPTY_OBJECT_MARKER}
        </span>
      </div>
    );
  }

  return (
    <div>
      <button
        className="py-1 px-2 hover:bg-slate-50 cursor-pointer flex items-center w-full text-left"
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        type="button">
        <FontAwesomeIcon
          icon={isExpanded ? faChevronDown : faChevronRight}
          className="text-slate-400 text-xs mr-2 w-3"
        />
        <span className="font-mono text-xs text-slate-600 font-bold mr-1">{fieldKey}:</span>
        {isArray && <span className="text-xs text-slate-400 italic">Array [{value.length}]</span>}
        {isObject && !isExpanded && (
          <span className="text-xs text-slate-400 italic">
            {`{${Object.keys(value as object).length}}`}
          </span>
        )}
      </button>

      {isExpanded && (
        <div>
          {isArray
            ? value.map((item: unknown, index: number) => (
                <CollapsibleField
                  key={`${fieldKey}[${index}]`}
                  fieldKey={`[${index}]`}
                  value={item}
                  depth={depth + 1}
                  showNull={showNull}
                  expandAll={expandAll}
                />
              ))
            : Object.entries(value as object)
                .filter(([_key, val]) => showNull || val !== null)
                .map(([key, val]) => (
                  <CollapsibleField
                    key={key}
                    fieldKey={key}
                    value={val}
                    depth={depth + 1}
                    showNull={showNull}
                    expandAll={expandAll}
                  />
                ))}
        </div>
      )}
    </div>
  );
};

export default CollapsibleField;
